import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FileInput } from '@components/FileInput/FileInput';
import { summarizeFile } from '../../../../api/upload';
import {
  CloseIcon,
  MuteIcon,
  ShareScreenIcon,
  StopIcon,
  UnmuteIcon,
} from '../../../../components/Icons';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { uploadFile } from '../../../../utils/fileUploader';
import { DataChannelMessage } from '../../hooks/useDataChannel';

interface PiPControlsProps {
  // Audio controls
  volume: number;
  isMuted: boolean;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;

  // Microphone controls
  isMicMuted?: boolean;
  onMicMute: (muted: boolean) => void;

  // Session controls
  isAuthenticated: boolean;
  isScreenSharing: boolean;
  onScreenShareClick: () => void;
  onEndSession: () => void;
  // Avatar talking state (passed from parent)
  isAvatarTalking?: boolean;
  isFileUploading: boolean;

  // Stop talking controls
  onStopTalking?: () => void;
  sendMessage?: (message: DataChannelMessage) => void;
  sessionId?: string;

  // Upload controls
  avatarId: string;
  organizationId?: string;
  onTrackUploadEvent?: (event: string) => void;
  showScreenShareButton: boolean;
  isWatchSession: boolean;
  socketId?: string;
}

export const PiPControls: React.FC<PiPControlsProps> = ({
  volume,
  isMuted,
  onVolumeChange,
  onMuteToggle,
  isMicMuted: initialMicMuted,
  onMicMute,
  isAuthenticated,
  isScreenSharing,
  onScreenShareClick,
  onEndSession,
  isAvatarTalking = false,
  onStopTalking,
  sendMessage,
  sessionId,
  avatarId,
  organizationId,
  onTrackUploadEvent,
  isFileUploading = false,
  showScreenShareButton,
  isWatchSession,
  socketId,
}) => {
  const { t } = useTranslation();
  const [isMicMuted, setIsMicMuted] = React.useState(initialMicMuted);

  const [file, setFile] = useState<File | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [visibleButtons, setVisibleButtons] = useState<string[]>([]);
  const [hiddenButtons, setHiddenButtons] = useState<string[]>([]);
  const [showStopButton, setShowStopButton] = useState(isAvatarTalking);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // Define button order (left to right, hide from right to left)
  const buttonOrder = useMemo(
    () => [
      ...(!isWatchSession ? ['micMute'] : []),
      'volume',
      ...(isAuthenticated && !isWatchSession ? ['upload'] : []),
      'screenShare',
      ...(showStopButton && !isWatchSession ? ['stopTalking'] : []),
      'endSession',
    ],
    [isAuthenticated, showStopButton],
  );

  const handleMicMute = () => {
    onMicMute(!isMicMuted);
    setIsMicMuted(!isMicMuted);
  };

  // Sync stop button visibility with avatar talking state (like Controls.tsx)
  useEffect(() => {
    if (isAvatarTalking) {
      setShowStopButton(true);
      return;
    }
    // Hide button with delay like main page
    const timeout = setTimeout(() => {
      setShowStopButton(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [isAvatarTalking]);

  // Calculate which buttons should be visible based on PiP window width
  const calculateVisibleButtons = useCallback(() => {
    // Get PiP window width instead of container width
    const pipWindow = containerRef.current?.ownerDocument?.defaultView;
    const windowWidth = pipWindow?.innerWidth || 400;

    // Calculate based on window width, not container width
    let maxVisibleButtons = buttonOrder.length;

    // Simple breakpoints based on PiP window width
    if (windowWidth < 300) {
      maxVisibleButtons = 3;
    } else if (windowWidth < 350) {
      maxVisibleButtons = 4;
    } else if (windowWidth < 400) {
      maxVisibleButtons = 5;
    }
    // else show all buttons

    if (maxVisibleButtons >= buttonOrder.length) {
      // All buttons fit
      setVisibleButtons(buttonOrder);
      setHiddenButtons([]);
    } else {
      // Some buttons need to be hidden
      const visible = buttonOrder.slice(0, maxVisibleButtons - 1); // Reserve space for more button
      const hidden = buttonOrder.slice(maxVisibleButtons - 1);

      setVisibleButtons(visible);
      setHiddenButtons(hidden);
    }
  }, [buttonOrder]);

  const handleStopTalking = () => {
    if (sendMessage) {
      sendMessage({
        type: 'cancel',
        data: { sessionId },
      });
    }

    onStopTalking?.();
  };

  // TODO: DRY this up with UploadInput.tsx
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      const newFile = files[0];
      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
      const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];

      if (newFile.size > MAX_FILE_SIZE) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        console.warn('PiP: File size should be less than 20MB');
        return;
      }

      if (!allowedTypes.includes(newFile.type)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        console.warn('PiP: Invalid file type');
        return;
      }

      setFile(newFile);
      return;
    }
    setFile(null);
  };

  const uploadAndSummarizeFile = async (newFile: File) => {
    if (!organizationId) {
      console.warn('PiP: Missing organizationId for upload');
      return;
    }

    try {
      onTrackUploadEvent?.('upload_started');
      const response = await uploadFile(newFile, organizationId);
      if (response?.id) {
        const resp = await summarizeFile({
          mentorId: avatarId,
          uploadId: response.id,
          socketId: socketId || '',
        });
        if (!resp.success) {
          throw new Error('Failed to summarize file');
        }
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('PiP: File upload failed:', error);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      onTrackUploadEvent?.('upload_completed');
    }
  };

  useEffect(() => {
    if (file != null) {
      uploadAndSummarizeFile(file).catch((error) => {
        console.error('PiP: Error in file upload:', error);
      });
    }
  }, [file]);

  // Set up ResizeObserver to watch PiP window size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const pipWindow = containerRef.current.ownerDocument?.defaultView;
    if (!pipWindow) return;

    const handleResize = () => {
      setShowDropdown(false); // Close dropdown on resize
      setTimeout(calculateVisibleButtons, 100);
    };

    pipWindow.addEventListener('resize', handleResize);

    return () => {
      pipWindow.removeEventListener('resize', handleResize);
    };
  }, [calculateVisibleButtons]);

  // Initialize visible buttons on mount and when authentication changes
  useEffect(() => {
    setVisibleButtons(buttonOrder);
    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(calculateVisibleButtons, 100);
    return () => clearTimeout(timer);
  }, [buttonOrder, calculateVisibleButtons]);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => {
    setShowDropdown(false);
  });

  // Render individual button components
  const renderButton = (buttonId: string) => {
    switch (buttonId) {
      case 'micMute':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon w-[40px] align-middle"
            onClick={handleMicMute}
            title={isMicMuted ? t('avatar.unmuteMicrophone') : t('avatar.muteMicrophone')}>
            {isMicMuted ? <MuteIcon /> : <UnmuteIcon />}
          </button>
        );

      case 'volume':
        return (
          <div key={buttonId} className="relative tc-mv-volume-wrapper">
            <div className="absolute flex justify-center bottom-full left-0 right-0 rounded-full pb-2">
              <div className="w-8 flex items-center justify-center rounded-full tc-mv-volume p-2">
                <input
                  type="range"
                  className="w-2 rounded-md h-full appearance-none bg-transparent cursor-pointer tc-mv-volume-slider"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                  style={{
                    background: `linear-gradient(to top, white 0%, white ${
                      (isMuted ? 0 : volume) * 100
                    }%, rgba(0,0,0,0.4) ${
                      (isMuted ? 0 : volume) * 100
                    }%, rgba(0,0,0,0.4) 100%)`,
                  }}
                />
              </div>
            </div>
            <button
              className="tc-waveform-icon w-[40px] align-middle text-white"
              onClick={onMuteToggle}
              title={isMuted ? t('avatar.unmute') : t('avatar.mute')}>
              {isMuted || volume === 0 ? (
                <i className="fa-solid fa-volume-xmark" />
              ) : (
                <i className="fa-solid fa-volume-high" />
              )}
            </button>
          </div>
        );

      case 'upload':
        return isAuthenticated ? (
          <div key={buttonId}>
            <FileInput
              isFileUploading={isFileUploading}
              handleFileChange={handleFileChange}
              ref={fileInputRef}
            />
          </div>
        ) : null;

      case 'screenShare':
        return showScreenShareButton ? (
          <button
            key={buttonId}
            className={`tc-waveform-icon w-[40px] align-middle ${
              isScreenSharing ? 'text-black !bg-white' : 'text-white'
            }`}
            onClick={onScreenShareClick}
            title={isScreenSharing ? t('avatar.stopScreenShare') : t('avatar.shareScreen')}>
            <ShareScreenIcon />
          </button>
        ) : null;

      case 'stopTalking':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-stop flex-shrink-0 talking"
            onClick={handleStopTalking}
            title={t('avatar.stopTalking')}>
            <StopIcon />
          </button>
        );

      case 'endSession':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-call-end flex-shrink-0 text-white w-[40px]"
            onClick={onEndSession}
            title={t('avatar.endSession')}>
            <CloseIcon />
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="tc-waveform-wrapper align-middle expanded">
        <div
          ref={containerRef}
          className="tc-chat-question-bar-container align-between relative">
          {/* Render visible buttons */}
          {visibleButtons.map((buttonId) => renderButton(buttonId))}

          {/* More button with dropdown for hidden buttons */}
          {hiddenButtons.length > 0 && (
            <div className="relative">
              <button
                ref={moreButtonRef}
                className="tc-waveform-icon w-[40px] align-middle text-white"
                onClick={() => setShowDropdown(!showDropdown)}
                title={t('avatar.moreOptions')}>
                <i className="fa-solid fa-ellipsis" />
              </button>

              {/* Dropdown menu (opens upward) */}
              {showDropdown && (
                <div className="absolute tc-mv-blur !backdrop-blur-none bottom-full !bg-danger left-1/2 transform -translate-x-1/2 mb-2 rounded-lg  z-50">
                  <div className="p-2 flex flex-col gap-2">
                    {hiddenButtons.map((buttonId) => renderButton(buttonId))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
